import type { AxiosError, AxiosRequestConfig, RawAxiosRequestHeaders } from "axios";
import { getAxiosInstance } from "./axiosInstance";
import { ensureConfig, getPublicPaths } from "./config";

type ApiErrorResponse = {
  code?: string;
  message?: string;
};

type ApiRequestConfig<T = any> = AxiosRequestConfig<T> & {
  isMultipart?: boolean;
  skipAuth?: boolean;
  _retry?: boolean;
};

const isRefreshTokenRequest = (url?: string) => (url ? url.includes("/token/refresh") : false);

const isAxiosError = (err: any): err is AxiosError & { config: ApiRequestConfig } => {
  return err && typeof err === "object" && "config" in err;
};

const resolveMaybePromise = async <T>(value: Promise<T> | T): Promise<T> => Promise.resolve(value);

const logoutOnError = async (error: unknown) => {
  const cfg = ensureConfig();
  await resolveMaybePromise(cfg.storage.clear());

  if (cfg.platform?.onLogout) {
    await cfg.platform.onLogout();
  }

  if (cfg.platform?.redirectToLogin) {
    cfg.platform.redirectToLogin();
  }

  if (cfg.platform?.restartApp) {
    cfg.platform.restartApp();
  }

  throw error;
};

export const refreshAccessToken = async () => {
  const cfg = ensureConfig();
  const refreshToken = await resolveMaybePromise(cfg.storage.getRefreshToken());

  if (!refreshToken) return null;

  try {
    const response = await getAxiosInstance().request<{ access: string; refresh: string }>({
      method: "post",
      url: "/accounts/token/refresh/",
      data: { refresh: refreshToken },
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    await resolveMaybePromise(cfg.storage.setAccessToken(response.data.access));

    if (response.data.refresh) {
      await resolveMaybePromise(cfg.storage.setRefreshToken(response.data.refresh));
    }

    return response.data;
  } catch (_error) {
    return null;
  }
};

export const apiService = async <T = any>({ isMultipart, skipAuth, ...request }: ApiRequestConfig): Promise<T> => {
  const cfg = ensureConfig();
  const publicPaths = getPublicPaths();

  const contentTypeHeaders: RawAxiosRequestHeaders = isMultipart
    ? { "Content-Type": "multipart/form-data" }
    : { "Content-Type": "application/json" };

  const headers: RawAxiosRequestHeaders = {
    ...contentTypeHeaders,
    ...(request.headers as RawAxiosRequestHeaders | undefined),
  };

  const isPublicRequest = request.url ? publicPaths.some((route) => request.url?.includes(route)) : false;

  if (!skipAuth && !isPublicRequest) {
    const token = await resolveMaybePromise(cfg.storage.getAccessToken());
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  try {
    const response = await getAxiosInstance().request<T>({ ...request, headers });
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse> & { config: ApiRequestConfig };
    const status = axiosError.response?.status;
    const errorCode = axiosError.response?.data?.code;

    if (isRefreshTokenRequest(request.url) || status === 403 || errorCode === "user_not_found") {
      await logoutOnError(error);
    }

    if (!isAxiosError(error)) {
      throw error;
    }

    if (status === 401 && isPublicRequest) {
      throw error;
    }

    if (status === 401 && !axiosError.config?._retry) {
      axiosError.config = axiosError.config ?? {};
      axiosError.config._retry = true;

      const refreshed = await refreshAccessToken();

      if (refreshed) {
        return apiService<T>({
          ...axiosError.config,
          headers: axiosError.config.headers as RawAxiosRequestHeaders,
          isMultipart: axiosError.config.isMultipart,
          skipAuth: axiosError.config.skipAuth,
        });
      }

      await logoutOnError(error);
    }

    throw error;
  }
};

export type { ApiRequestConfig };
